import { assertNever, LanguageCode, languageValidation, SurveyQuestion, SurveyQuestionConfig } from "../..";
import { surveyQuestionFieldValidation } from "../fieldValidation/fields/surveyQuestionFieldValidation";
import { surveyQuestionOptionObjectValidation, SurveyQuestionOptionObjectValidationResult } from './surveyQuestionOptionObjectValidation';

export type SurveyQuestionObjectValidationResult =
      'invalid_questionList'
    | 'invalid_question'
    | 'invalid_question_indicators'
    | 'invalid_question_title'
    | 'invalid_question_canComment'
    | 'invalid_question_config'
    | 'invalid_question_type'
    | 'invalid_question_invertScore'
    | 'invalid_question_scored'
    | 'invalid_question_translation'
    | 'invalid_question_option'
    | 'invalid_question_option_translation'
    | 'invalid_question_option_count'
    | SurveyQuestionConfigObjectValidationResult;

export type SurveyQuestionConfigObjectValidationResult = 
    'valid' |
    'invalid_question_config' |
    'invalid_question_type' |
    'invalid_question_invertScore' |
    'invalid_question_scored' |
    SurveyQuestionOptionObjectValidationResult;

function validateConfig(value:SurveyQuestionConfig, requiredTranslations:LanguageCode[]): SurveyQuestionConfigObjectValidationResult
{
    const typeValidation = surveyQuestionFieldValidation.surveyQuestion.type(value?.type);
    if(!typeValidation)
        return 'invalid_question_type';

    if(value.type === 'default')
    {
        const allowedFields = ['type', 'invertScore'];
        if(Object.keys(value).find(k => !allowedFields.includes(k)))
            return 'invalid_question_config';

        if(!surveyQuestionFieldValidation.surveyQuestion.invertScore(value.invertScore))
            return 'invalid_question_invertScore';

        return 'valid';
    }

    else if(value.type === 'custom')
    {
        const allowedFields = ['type', 'scored', 'options'];
        if(Object.keys(value).find(k => !allowedFields.includes(k)))
            return 'invalid_question_config';

        if(!surveyQuestionFieldValidation.surveyQuestion.scored(value.scored))
            return 'invalid_question_scored';

        return surveyQuestionOptionObjectValidation.validateConfigOptions(value, requiredTranslations);
    }

    else if(value.type === 'multiple-choice')
    {
        const allowedFields = ['type', 'options'];
        if(Object.keys(value).find(k => !allowedFields.includes(k)))
            return 'invalid_question_config';

        return surveyQuestionOptionObjectValidation.validateConfigOptions(value, requiredTranslations);
    }

    else {
        assertNever(value);
        throw new Error('Unsupported question type');
    }
}

function validateQuestion(value:SurveyQuestion, requiredTranslations:LanguageCode[]): SurveyQuestionObjectValidationResult {
    if(value === null || value === undefined || typeof value !== 'object')
        return 'invalid_question';

    if(!surveyQuestionFieldValidation.surveyQuestion.indicators(value.indicators as string[]))
        return 'invalid_question_indicators';

    if(!surveyQuestionFieldValidation.surveyQuestion.title(value.title as string))
        return 'invalid_question_title';

    if(!surveyQuestionFieldValidation.surveyQuestion.canComment(value.canComment as boolean))
        return 'invalid_question_canComment';

    if(!surveyQuestionFieldValidation.surveyQuestion.conditionalDisplay(value.conditionalDisplay))
        return 'invalid_question_config';

    if(!languageValidation.identicalLanguageCodes(
        requiredTranslations, 
        value.translations.map(t => t.languageCode)
    ))
        return 'invalid_question_translation';

    if(value.translations.some(t => !surveyQuestionFieldValidation.surveyQuestion.title(t.text)))
        return 'invalid_question_translation';

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { indicators, title, canComment, translations, conditionalDisplay, ...config } = value;
    return validateConfig(config, requiredTranslations);
}

function validateQuestionList(value: unknown, requiredTranslations:LanguageCode[]): SurveyQuestionObjectValidationResult {
    if(!Array.isArray(value))
        return 'invalid_questionList';

    for(let i=0; i<value.length; i++)
    {
        const validation = validateQuestion(value[i], requiredTranslations);
        if(validation !== 'valid')
            return validation;
    }

    return 'valid';
}

export const surveyQuestionObjectValidation = {
    validateConfig: validateConfig,
    question:validateQuestion,
    questionList: validateQuestionList
};
